import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { ComponentProps } from 'react';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { LinkOther } from '@sorare/core/src/atoms/navigation/Box';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { TrophyIcon } from '@sorare/core/src/components/club/Trophies';
import { Avatar } from '@sorare/core/src/components/user/Avatar';
import { UserName } from '@sorare/core/src/components/user/UserName';
import { isType, isTypeFn } from '@sorare/core/src/lib/gql';

import { AnyCardReward } from 'components/rewards/AnyCardReward';
import { ClickableAnyRewardConfig } from 'components/rewards/ClickableAnyRewardConfig';
import { Variant } from 'components/rewards/RewardOverview/types';

import { So5LeaderboardWrapper } from '../So5LeaderboardWrapper';
import type { LivePastLeaderboard_so5Leaderboard } from './__generated__/index.graphql';

const TrophyWrapper = styled.div`
  min-width: var(--triple-unit);
`;
const StyledSBHorizontal = styled(SBHorizontal)`
  flex-wrap: wrap;
  column-gap: var(--double-unit);

  & > *:first-child {
    max-width: 160px;
  }
`;
const StyledLinkOther = styled(LinkOther)`
  transition: opacity 0.2s ease-in-out;
  &:hover {
    opacity: 0.5;
  }
`;

type Props = {
  so5Leaderboard: LivePastLeaderboard_so5Leaderboard;
  onClickRanking?: (contender: string) => void;
} & Pick<ComponentProps<typeof So5LeaderboardWrapper>, 'onClick'>;

export const LivePastLeaderboard = ({
  so5Leaderboard,
  onClick,
  onClickRanking,
}: Props) => {
  return (
    <So5LeaderboardWrapper so5Leaderboard={so5Leaderboard} onClick={onClick}>
      <Vertical gap={2}>
        {so5Leaderboard.so5RankingsPreview.map(ranking => {
          const rewardConfigs = ranking.eligibleOrSo5Rewards.flatMap(
            eligibleOrSo5Reward =>
              isType(eligibleOrSo5Reward, 'So5Reward')
                ? eligibleOrSo5Reward.rewardConfigs.filter(
                    config => !isType(config, 'CardRewardConfig')
                  )
                : eligibleOrSo5Reward.rewardConfigs
          );

          const anyCardRewards = ranking.eligibleOrSo5Rewards
            .filter(isTypeFn('So5Reward'))
            .flatMap(eligibleOrSo5Reward =>
              eligibleOrSo5Reward.rewards.filter(isTypeFn('AnyCardReward'))
            );

          return (
            <StyledSBHorizontal key={ranking.id}>
              <StyledLinkOther
                as={ButtonBase}
                onClick={() =>
                  onClickRanking?.(
                    ranking.so5Lineup.so5LeaderboardContender.slug
                  )
                }
                key={ranking.id}
              >
                <Horizontal gap={2}>
                  {!!ranking.ranking && (
                    <TrophyWrapper>
                      <TrophyIcon ranking={ranking.ranking} />
                    </TrophyWrapper>
                  )}
                  <Horizontal>
                    <Avatar rounded user={ranking.so5Lineup.user} size={3} />
                    <LabelM bold>
                      <UserName user={ranking.so5Lineup.user} />
                    </LabelM>
                  </Horizontal>
                </Horizontal>
              </StyledLinkOther>
              <Horizontal>
                {anyCardRewards.map(anyCardReward => (
                  <AnyCardReward
                    key={anyCardReward.id}
                    anyCardReward={anyCardReward}
                    variant={Variant.INLINE}
                  />
                ))}
                {rewardConfigs.map(rewardConfig => (
                  <ClickableAnyRewardConfig
                    key={rewardConfig.id}
                    anyRewardConfig={rewardConfig}
                    so5League={so5Leaderboard.so5League}
                    variant={Variant.OVERVIEW_INLINE}
                  />
                ))}
              </Horizontal>
            </StyledSBHorizontal>
          );
        })}
      </Vertical>
    </So5LeaderboardWrapper>
  );
};

LivePastLeaderboard.fragments = {
  so5Leaderboard: gql`
    fragment LivePastLeaderboard_so5Leaderboard on So5Leaderboard {
      slug
      so5League {
        slug
        ...ClickableAnyRewardConfig_so5League
      }
      so5RankingsPreview(size: 3) {
        id
        ranking
        so5Lineup {
          id
          so5LeaderboardContender {
            slug
          }
          user {
            slug
            ...Avatar_publicUserInfoInterface
            ...UserName_publicUserInfoInterface
          }
        }
        eligibleOrSo5Rewards {
          ... on So5Reward {
            slug
            rewards {
              id
              ... on AnyCardReward {
                id
                card {
                  slug
                  anyPlayer {
                    slug
                    displayName
                  }
                }
                ...AnyCardReward_anyCardReward
              }
            }
            rewardConfigs {
              id
              ...ClickableAnyRewardConfig_anyRewardConfigInterface
            }
          }
          ... on So5RewardConfig {
            rewardConfigs {
              id
              ...ClickableAnyRewardConfig_anyRewardConfigInterface
            }
          }
        }
      }
      ...So5LeaderboardWrapper_so5Leaderboard
    }
    ${So5LeaderboardWrapper.fragments.so5Leaderboard}
    ${Avatar.fragments.publicUserInfoInterface}
    ${UserName.fragments.user}
    ${ClickableAnyRewardConfig.fragments.anyRewardConfigInterface}
    ${ClickableAnyRewardConfig.fragments.so5League}
    ${AnyCardReward.fragments.anyCardReward}
  ` as TypedDocumentNode<LivePastLeaderboard_so5Leaderboard>,
};
